/**
 * EditReservationDialog — Ted #25 (#986).
 *
 * > "Today, their reservation showed Daycare, and I could not find a way to
 * > change the existing reservation from Daycare → Hike." — Ted, 2026-09-15
 *
 * The desk changes a live reservation's service, date(s) and drop-off /
 * pick-up times without cancelling and recreating it. PR 1 of the #918 spec,
 * staff path.
 *
 * THE RULE THIS COMPONENT OBEYS (spec §2.3): **the server prices; this dialog
 * only previews.** Every field change asks `POST …/edit/preview`, which
 * re-runs every create-time gate and re-prices — and writes nothing. The
 * number on screen is the server's. Commit re-derives it again, so a preview
 * that went stale while the receptionist was talking to an owner cannot
 * become the charge; it comes back as a refusal with the new number.
 *
 * Design (docs/DESIGN-PHILOSOPHY.md):
 *  - Counter Test: Kylie, mid-morning with a dog on the leash, opens the row,
 *    taps the new service, reads "Half-Day Daycare — $22.00 less. New total
 *    $25.00," and taps Save. Two taps and a number, no training.
 *  - Principle 2 (the next action is obvious): exactly ONE filled-primary
 *    button in the panel. Cancel is ghost.
 *  - Principle 5 (never lie about data): a refusal REPLACES the price line —
 *    the desk never sees a price next to a change the server just refused —
 *    and the refusal is the server's own sentence, not client-invented copy.
 *  - Five states: default · loading (the preview line says "Checking…") ·
 *    empty (nothing changed yet — the primary is disabled with a reason) ·
 *    error (the server's sentence + the form stays open to fix it) · stale is
 *    structurally impossible here (every render re-previews; there is no
 *    cached number to age).
 *  - Tokens only, 44px targets, one column at 375.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  editReservation,
  previewReservationEdit,
  type AdminBooking,
  type ReservationEditPatch,
  type ReservationEditResult,
  type ReservationEditPreview,
  type ServiceTypeSummary,
  type TransportMode,
  TRANSPORT_MODE_LABELS,
} from '../../lib/api.js';
import { isGroomingServiceType } from '../../lib/groomingTiers.js';
import './EditReservationDialog.css';
// Sawyer 07 quickbook-bed-addons (B1) — the stay's bed, and the bed following a dates edit.
import { adminLodgingAssignApi, readSession, type ApiResponse, type LodgingUnit } from '../../lib/api.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';
import {
  bedWriteSentence,
  canPlaceBeds,
  draftPlacement,
  planBedWrite,
  stayBedOnRead,
  unionReadRange,
  type BedWrite,
} from '../../lib/booking-bed.js';
import { BookingBedField } from './BookingBedField.js';
import { useLodgingRead } from './useLodgingRead.js';

export interface EditReservationDialogProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly booking: Pick<
    AdminBooking,
    'id' | 'service_type_id' | 'date' | 'status'
  > & {
    start_date?: string | null;
    end_date?: string | null;
    drop_off_time?: string | null;
    pick_up_time?: string | null;
    /** Ted #28 (#1050) — transport on the stay; absent on an older server. */
    transport?: TransportMode;
    /** Sawyer 07 (B1) — fallback family for the bed verdict; the lodging read is preferred (refuter C-6). */
    customer_id?: string | null;
  };
  /** Label for the header — the dog(s), never the booking's uuid. */
  readonly label: string;
  /** Sawyer 07 (B1) — the dog(s) alone, for the bed sentences ("Moves Charles to Dog 7."). Falls back to `label`. */
  readonly dogLabel?: string;
  readonly serviceTypes: readonly ServiceTypeSummary[];
  readonly onClose: () => void;
  /** Fired after a successful commit so the caller can refresh its list. */
  readonly onSaved: (summary: string) => void;
  /**
   * Ted #30 (#1109 AC3, lane LR) — where the commit goes. Absent = the plain
   * edit route (`editReservation`). An occurrence of a recurring series is
   * committed through the series route with the scope the desk chose
   * ("this one" / "this and future"); the preview, the fields and the
   * refusal copy are unchanged — only the write differs.
   */
  readonly commit?: (patch: ReservationEditPatch) => Promise<ReservationEditResult>;
}

function money(cents: number): string {
  return `$${(Math.abs(cents) / 100).toFixed(2)}`;
}

/** An ISO instant or date from the server → the YYYY-MM-DD an <input> wants. */
function dateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

/** The one sentence the desk reads before committing. */
// Sawyer 07 quickbook-bed-addons (B1)
/** A server sentence, ending in a full stop. */
function asSentence(s: string): string {
  const t = s.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/** What the page notice says once the bed write landed. */
function bedDoneSentence(write: BedWrite, dog: string, names: { current: string | null; chosen: string | null }): string {
  switch (write.kind) {
    case 'none':
      return '';
    case 'assign':
      return `${dog} is on ${names.chosen ?? 'the chosen bed'}.`;
    case 'move':
      return names.current && names.current === names.chosen
        ? `${names.chosen} moved with the new dates.`
        : `${dog} moved to ${names.chosen ?? 'the chosen bed'}.`;
    case 'release':
      return `${dog} is off ${names.current ?? 'the bed'}.`;
  }
}

export function priceSentence(preview: ReservationEditPreview): string {
  const { deltaCents, newCents } = preview.price;
  if (deltaCents === 0) return `No change to the price — total stays ${money(newCents)}.`;
  const direction = deltaCents > 0 ? 'more' : 'less';
  return `${money(deltaCents)} ${direction}. New total ${money(newCents)}.`;
}

export function EditReservationDialog({
  apiUrl,
  token,
  booking,
  label,
  dogLabel,
  serviceTypes,
  onClose,
  onSaved,
  commit,
}: EditReservationDialogProps): JSX.Element {
  // What the booking IS today — fixed, the thing every patch is diffed against.
  const wasMultiDay = Boolean(booking.start_date && booking.end_date);
  // What the desk is asking it to BE. This is STATE, not a derivation of the
  // booking (review fix): without it Ted's own case — a Boutique Hotel stay the
  // client meant as one day of Daycare — was unreachable from this dialog. He
  // could change the service and still be left with a two-date range, priced
  // per night. The API has always taken either shape; nothing offered the
  // choice.
  const [isMultiDay, setIsMultiDay] = useState(wasMultiDay);
  const [serviceTypeId, setServiceTypeId] = useState(booking.service_type_id);
  // Seeded so a switch either way lands on a sensible day rather than an empty
  // field: a stay collapses onto its FIRST day, a single day expands into a
  // one-day range the desk then extends.
  const [date, setDate] = useState(
    dateInputValue(wasMultiDay ? booking.start_date : booking.date),
  );
  const [startDate, setStartDate] = useState(
    dateInputValue(booking.start_date ?? booking.date),
  );
  const [endDate, setEndDate] = useState(
    dateInputValue(booking.end_date ?? booking.date),
  );
  const [dropOffTime, setDropOffTime] = useState(booking.drop_off_time ?? '');
  const [pickUpTime, setPickUpTime] = useState(booking.pick_up_time ?? '');
  // Ted #28 (#1050) — transport on the stay. 'none' when the server predates
  // the field, which is also every row that never had one.
  const [transport, setTransport] = useState<TransportMode>(booking.transport ?? 'none');
  const [reason, setReason] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  /**
   * #718 override — round-4 review fix. The server refuses an edit whose dog
   * has no completed assessment with 409 `assessment_required`, naming
   * `assessmentOverrideReason` as the way through. The dialog had no such
   * field, so the desk read a sentence telling them to send something they
   * could not send, and cancel-and-recreate was the only way out — the
   * workflow this PR exists to delete. `assessmentBlocked` LATCHES: once the
   * server has asked for the override the field stays on screen, so it does
   * not vanish the moment the override makes the preview succeed.
   */
  const [assessmentBlocked, setAssessmentBlocked] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  /**
   * Principle 5 (never offer what cannot happen). The server refuses grooming
   * as a CLASS on every preview — it carries its own sub-service, size matrix,
   * slot and groomer (spec §12 E6) — so an unfiltered list taught the desk the
   * rule by making them hit a 409 with an owner standing there. Review fix
   * (#986, round 3): the list is the tenant's active services MINUS grooming.
   * The booking's OWN service is kept whatever it is, so the control never
   * renders with a value that is not in its options.
   */
  const selectableServices = serviceTypes.filter(
    (s) => s.id === booking.service_type_id || !isGroomingServiceType(s.name),
  );

  const [preview, setPreview] = useState<ReservationEditPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Sawyer 07 quickbook-bed-addons (B1)
  // ── The stay's bed. Only for roles that may place one (refuter C-14: the
  // dialog reads the session itself, as Quick Book does); floor roles see no
  // field and send no lodging read (M3).
  const sessionRoles = effectiveRolesOf(readSession().staff);
  const canAssignBeds = canPlaceBeds(sessionRoles);
  const canEditBeds =
    rolesHavePermission(sessionRoles, 'business-config.lodging:write:all') ||
    rolesHavePermission(sessionRoles, 'business-config:write:all');
  const isBoardingType = (id: string) => serviceTypes.find((t) => t.id === id)?.name === 'boarding';
  const wasBoardingStay = wasMultiDay && isBoardingType(booking.service_type_id);
  const bedApplies = isMultiDay && isBoardingType(serviceTypeId) && Boolean(startDate && endDate && endDate > startDate);
  // Refuter C-5 — ONE read over the old nights (to find the current bed) and
  // the new ones (to judge what is free), even for a non-overlapping move.
  const oldSpan = wasBoardingStay
    ? { startDate: dateInputValue(booking.start_date), endDate: dateInputValue(booking.end_date) }
    : null;
  const newSpan = bedApplies ? { startDate, endDate } : null;
  const bedRead = useLodgingRead(apiUrl, canAssignBeds ? unionReadRange(oldSpan, newSpan) : null);
  // Refuter C-6 — the family and the dogs come from the read (every caller
  // of this dialog), the prop only as a fallback.
  const stay = stayBedOnRead(bedRead.data, booking.id);
  // null = the desk has not touched the select: the current bed stands.
  const [bedChoice, setBedChoice] = useState<string | null>(null);
  const chosenUnitId = bedChoice ?? stay?.unitId ?? '';
  const bedPlacement = bedApplies
    ? draftPlacement({
        customerId: stay?.customerId ?? booking.customer_id ?? null,
        dogCount: stay?.dogCount ?? 1,
        startDate,
        endDate,
        assignmentId: stay?.assignmentId ?? null,
      })
    : null;
  const bedWrite: BedWrite =
    canAssignBeds && bedRead.data
      ? planBedWrite({ current: stay, chosenUnitId, applies: bedApplies, span: { startDate, endDate } })
      : { kind: 'none' };
  const unitName = (id: string | null | undefined): string | null =>
    id ? (bedRead.data?.units.find((u: LodgingUnit) => u.id === id)?.name ?? null) : null;
  const bedNames = { current: stay?.unitName ?? null, chosen: unitName(chosenUnitId) };
  const dog = dogLabel ?? label;
  const bedReason = bedWriteSentence(bedWrite, dog, bedNames);

  // The patch as the server will receive it: only fields that actually moved.
  // Sending an unchanged field would be harmless but would show up in the
  // ledger diff as a change, and the desk reads that diff back.
  const buildPatch = useCallback((): ReservationEditPatch => {
    const patch: ReservationEditPatch = {};
    if (serviceTypeId !== booking.service_type_id) patch.serviceTypeId = serviceTypeId;
    // A CHANGE OF SHAPE IS ITSELF A CHANGE, even when the days read the same.
    // Collapsing a stay whose first day is 2026-10-01 onto 2026-10-01 nulls
    // start_date and end_date and re-prices per day instead of per night — so
    // the patch must carry `date` even though the calendar value did not move.
    // `date` and the range are mutually exclusive by the server's schema, so
    // exactly one branch may contribute.
    if (isMultiDay) {
      const shapeChanged = !wasMultiDay;
      if (startDate && (shapeChanged || startDate !== dateInputValue(booking.start_date))) {
        patch.startDate = startDate;
      }
      if (endDate && (shapeChanged || endDate !== dateInputValue(booking.end_date))) {
        patch.endDate = endDate;
      }
    } else if (date && (wasMultiDay || date !== dateInputValue(booking.date))) {
      patch.date = date;
    }
    const dropOff = dropOffTime || null;
    if (dropOff !== (booking.drop_off_time ?? null)) patch.dropOffTime = dropOff;
    const pickUp = pickUpTime || null;
    if (pickUp !== (booking.pick_up_time ?? null)) patch.pickUpTime = pickUp;
    if (transport !== (booking.transport ?? 'none')) patch.transport = transport;
    return patch;
  }, [
    booking.date,
    booking.drop_off_time,
    booking.end_date,
    booking.pick_up_time,
    booking.service_type_id,
    booking.start_date,
    booking.transport,
    date,
    dropOffTime,
    endDate,
    isMultiDay,
    pickUpTime,
    serviceTypeId,
    startDate,
    transport,
    wasMultiDay,
  ]);

  const patch = buildPatch();
  const hasChange = Object.keys(patch).length > 0;
  // Sent only once it can be ACCEPTED: the server's schema is 3–300 chars, so
  // holding it back below three stops every keystroke from coming back a 400.
  const override = overrideReason.trim().length >= 3 ? overrideReason.trim() : undefined;
  // The override is NOT part of `hasChange` — it authorises a change, it is
  // not one — but it IS part of what gets previewed, so the desk sees the
  // price the override unlocks before committing to it.
  const patchKey = JSON.stringify({
    ...patch,
    ...(override ? { assessmentOverrideReason: override } : {}),
  });

  useEffect(() => {
    if (!hasChange) {
      setPreview(null);
      setRefusal(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    previewReservationEdit(apiUrl, token, booking.id, JSON.parse(patchKey) as ReservationEditPatch)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        setRefusal(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPreview(null);
        // Branch on the CODE, never on the prose (#711). This is the one
        // refusal the desk can clear from inside this dialog.
        if (e instanceof ApiError && e.code === 'assessment_required') {
          setAssessmentBlocked(true);
        }
        // The server's own sentence. It already names the dog, the shot or the
        // day; re-wording it here would be a second source of truth.
        setRefusal(e instanceof Error ? e.message : 'We could not check this change.');
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, booking.id, hasChange, patchKey, token]);

  useEffect(() => {
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Sawyer 07 quickbook-bed-addons (B1) — the edit first, then the bed.
  async function runBedWrite(write: BedWrite): Promise<ApiResponse<unknown>> {
    switch (write.kind) {
      case 'none':
        return { data: null };
      case 'assign':
        return adminLodgingAssignApi.assign(apiUrl, { bookingId: booking.id, unitId: write.unitId, startDate: write.startDate, endDate: write.endDate });
      case 'move':
        return adminLodgingAssignApi.move(apiUrl, {
          replaceAssignmentId: write.assignmentId,
          bookingId: booking.id,
          unitId: write.unitId,
          startDate: write.startDate,
          endDate: write.endDate,
        });
      case 'release':
        return adminLodgingAssignApi.unassign(apiUrl, write.assignmentId);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    // 1 — the reservation edit (unchanged). A refused edit sends no bed call.
    let summary = '';
    if (hasChange) {
      try {
        const commitPatch: ReservationEditPatch = {
          ...patch,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(override ? { assessmentOverrideReason: override } : {}),
          ...(notifyCustomer ? { notifyCustomer: true } : {}),
        };
        const result = commit ? await commit(commitPatch) : await editReservation(apiUrl, token, booking.id, commitPatch);
        summary = `${label} updated — ${result.changes.length} change${
          result.changes.length === 1 ? '' : 's'
        }, new total ${money(result.price.afterCents)}.`;
      } catch (e: unknown) {
        if (e instanceof ApiError && e.code === 'assessment_required') {
          setAssessmentBlocked(true);
        }
        setSaveError(e instanceof Error ? e.message : 'We could not save this change.');
        setSaving(false);
        return;
      }
    }
    // 2 — the bed (Sawyer B1): the calendar's own write path.
    if (bedWrite.kind === 'none') {
      onSaved(summary);
      setSaving(false);
      onClose();
      return;
    }
    const written = await runBedWrite(bedWrite);
    if (!written.error) {
      const done = bedDoneSentence(bedWrite, dog, bedNames);
      onSaved(summary ? `${summary} ${done}` : done);
      setSaving(false);
      onClose();
      return;
    }
    const why = asSentence(written.error);
    if (!hasChange) {
      // Nothing was committed — the desk can pick again right here.
      setSaveError(why);
      setSaving(false);
      return;
    }
    // Refuter C-3 — the edit IS committed: never leave the dialog open to
    // re-send it. Say what happened to the bed on the page notice, and make
    // the calendar truthful (a bed on nights the dog is not here is the bug).
    let notice: string;
    if (bedWrite.kind === 'move') {
      const off = await adminLodgingAssignApi.unassign(apiUrl, bedWrite.assignmentId);
      notice = off.error
        ? `${summary} Its bed was not moved: ${why} Taking ${dog} off ${bedNames.current ?? 'the old bed'} also failed: ${asSentence(off.error)} Fix it on the Lodging calendar.`
        : `${summary} Its bed was not moved: ${why} ${dog} is under 'Not yet assigned to a bed' on the Lodging calendar.`;
    } else if (bedWrite.kind === 'assign') {
      notice = `${summary} The bed was not held: ${why} ${dog} is under 'Not yet assigned to a bed' on the Lodging calendar.`;
    } else {
      notice = `${summary} ${dog} is still on ${bedNames.current ?? 'the bed'}: ${why} Take ${dog} off it on the Lodging calendar.`;
    }
    onSaved(notice);
    setSaving(false);
    onClose();
  }

  // Sawyer 07 quickbook-bed-addons (B1) — a bed-only change is a change too.
  const canSave =
    !saving && (hasChange ? !previewing && preview !== null : bedWrite.kind !== 'none');

  return (
    <div
      className="ferro-edit-res__overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ferro-edit-res__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-reservation-title"
        tabIndex={-1}
        ref={panelRef}
        data-testid="edit-reservation-dialog"
      >
        <header className="ferro-edit-res__header">
          <h2 className="ferro-section__subtitle" id="edit-reservation-title">
            Edit reservation
          </h2>
          <p className="ferro-muted ferro-edit-res__label">{label}</p>
        </header>

        <label className="ferro-field">
          <span className="ferro-field__label">Service</span>
          <select
            className="ferro-field__input"
            value={serviceTypeId}
            disabled={saving}
            onChange={(e) => setServiceTypeId(e.target.value)}
            data-testid="edit-reservation-service"
          >
            {selectableServices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name}
              </option>
            ))}
          </select>
        </label>

        {/* The shape of the stay. Ted's case is exactly this control: the
            client booked a multi-night Boutique Hotel stay meaning one day of
            Daycare, and until now the desk could change the service but never
            the shape. Radios, not a checkbox — both options are named, so the
            desk reads what the reservation IS before deciding. */}
        <fieldset className="ferro-edit-res__stay" data-testid="edit-reservation-stay">
          <legend className="ferro-field__label">Length of stay</legend>
          <label className="ferro-edit-res__stay-option">
            <input
              type="radio"
              name="edit-reservation-stay"
              checked={!isMultiDay}
              disabled={saving}
              onChange={() => setIsMultiDay(false)}
              data-testid="edit-reservation-stay-single"
            />
            <span>One day</span>
          </label>
          <label className="ferro-edit-res__stay-option">
            <input
              type="radio"
              name="edit-reservation-stay"
              checked={isMultiDay}
              disabled={saving}
              onChange={() => setIsMultiDay(true)}
              data-testid="edit-reservation-stay-multi"
            />
            <span>Several days</span>
          </label>
        </fieldset>

        {isMultiDay ? (
          <div className="ferro-edit-res__row">
            <label className="ferro-field">
              <span className="ferro-field__label">First day</span>
              <input
                type="date"
                className="ferro-field__input"
                value={startDate}
                disabled={saving}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="edit-reservation-start-date"
              />
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Last day</span>
              <input
                type="date"
                className="ferro-field__input"
                value={endDate}
                disabled={saving}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="edit-reservation-end-date"
              />
            </label>
          </div>
        ) : (
          <label className="ferro-field">
            <span className="ferro-field__label">Date</span>
            <input
              type="date"
              className="ferro-field__input"
              value={date}
              disabled={saving}
              onChange={(e) => setDate(e.target.value)}
              data-testid="edit-reservation-date"
            />
          </label>
        )}

        {/* Sawyer 07 quickbook-bed-addons (B1) — the stay's bed, below its dates. */}
        {canAssignBeds && bedApplies ? (
          <BookingBedField
            read={bedRead}
            hasParty
            overnight={bedApplies}
            placement={bedPlacement}
            value={chosenUnitId}
            onChange={(unit) => setBedChoice(unit?.id ?? '')}
            current={stay?.unitId && stay.unitName ? { unitId: stay.unitId, unitName: stay.unitName } : null}
            canEditBeds={canEditBeds}
            context="edit"
            disabled={saving}
          />
        ) : null}
        {bedReason ? (
          <p className="ferro-muted" role="status" data-testid="edit-reservation-bed-reason">
            {bedReason}
          </p>
        ) : null}

        <div className="ferro-edit-res__row">
          <label className="ferro-field">
            <span className="ferro-field__label">Drop-off</span>
            <input
              type="time"
              className="ferro-field__input"
              value={dropOffTime}
              disabled={saving}
              onChange={(e) => setDropOffTime(e.target.value)}
              data-testid="edit-reservation-drop-off"
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Pick-up</span>
            <input
              type="time"
              className="ferro-field__input"
              value={pickUpTime}
              disabled={saving}
              onChange={(e) => setPickUpTime(e.target.value)}
              data-testid="edit-reservation-pick-up"
            />
          </label>
        </div>

        {/* Ted #28 (#1050) — transport on the stay. "Transport in" makes the
            check-in ask whether the dog is physically here; "Transport home"
            flags the checkout. A plain select: four options, no guessing. */}
        <label className="ferro-field">
          <span className="ferro-field__label">Transport</span>
          <select
            className="ferro-field__input"
            value={transport}
            disabled={saving}
            onChange={(e) => setTransport(e.target.value as TransportMode)}
            data-testid="edit-reservation-transport"
          >
            {(Object.keys(TRANSPORT_MODE_LABELS) as TransportMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {TRANSPORT_MODE_LABELS[mode]}
              </option>
            ))}
          </select>
        </label>

        {/* #718 — shown ONLY after the server has asked for it. Principle 5
            (never offer what cannot happen): on the tenants where assessments
            are dark this control never exists, and on the tenants where they
            are armed it appears at the exact moment the desk is stuck, with
            the server's own sentence above it. */}
        {assessmentBlocked ? (
          <label className="ferro-field" data-testid="edit-reservation-override-field">
            <span className="ferro-field__label">
              Override the assessment requirement (logged against you)
            </span>
            <input
              type="text"
              className="ferro-field__input"
              value={overrideReason}
              minLength={3}
              maxLength={300}
              disabled={saving}
              placeholder="Owner brought vet records; assessed on the spot"
              onChange={(e) => setOverrideReason(e.target.value)}
              data-testid="edit-reservation-override"
            />
          </label>
        ) : null}

        <label className="ferro-field">
          <span className="ferro-field__label">Why (optional — kept on the record)</span>
          <input
            type="text"
            className="ferro-field__input"
            value={reason}
            maxLength={500}
            disabled={saving}
            onChange={(e) => setReason(e.target.value)}
            data-testid="edit-reservation-reason"
          />
        </label>

        {/* #915 — off unless the desk says so. An edit is usually a correction
            made with the owner standing right there. */}
        <label className="ferro-edit-res__notify">
          <input
            type="checkbox"
            checked={notifyCustomer}
            disabled={saving}
            onChange={(e) => setNotifyCustomer(e.target.checked)}
            data-testid="edit-reservation-notify"
          />
          <span>Send the client an updated confirmation</span>
        </label>

        {/* The one line that decides the tap. Exactly one of: teaching copy,
            "checking", the server's refusal, or the server's price. */}
        <div
          className={`ferro-edit-res__preview${
            refusal ? ' ferro-edit-res__preview--refused' : ''
          }`}
          role="status"
          aria-live="polite"
          data-testid="edit-reservation-preview"
        >
          {!hasChange ? (
            <p>Change the service, the date or a time to see the new price.</p>
          ) : previewing ? (
            <p>Checking availability and price…</p>
          ) : refusal ? (
            <p>{refusal}</p>
          ) : preview ? (
            <>
              <p className="ferro-edit-res__price">{priceSentence(preview)}</p>
              <ul className="ferro-edit-res__changes">
                {preview.changes.map((c) => (
                  <li key={c.field}>{describeChange(c.field)}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>

        {saveError ? (
          <p className="ferro-edit-res__error" role="alert" data-testid="edit-reservation-error">
            {saveError}
          </p>
        ) : null}

        <div className="ferro-edit-res__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="edit-reservation-cancel"
          >
            Close
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => void handleSave()}
            disabled={!canSave}
            data-testid="edit-reservation-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Field names the desk can read. Never the column name. */
function describeChange(field: string): string {
  switch (field) {
    case 'service_type_id':
      return 'Service changes';
    case 'date':
      return 'Date changes';
    case 'start_date':
      return 'First day changes';
    case 'end_date':
      return 'Last day changes';
    case 'drop_off_time':
      return 'Drop-off time changes';
    case 'pick_up_time':
      return 'Pick-up time changes';
    case 'notes':
      return 'Notes change';
    case 'transport':
      return 'Transport changes';
    // Ted #28 Phase 1b — a transport line born at a price the desk set.
    case 'price':
      return 'Price changes';
    default:
      return 'Reservation changes';
  }
}
